import { isDev } from '@twistopayments/design-system/environment'

export type LogEvent = {
  message: string
  level: 'error' | 'warning' | 'info' | 'debug'
  extra?: Record<string, unknown>
}

export type Logger = {
  captureEvent: (event: LogEvent) => void
  captureException: (error: Error, context?: Record<string, unknown>) => void
}

// Composite logger that can use multiple logging platforms.
// The host app registers concrete implementations (Sentry, Datadog, etc.)
// via `registerLogger`. The design-system package no longer ships a
// hard-coded Sentry dependency — that's a host-app integration.
class CompositeLogger implements Logger {
  private loggers: Logger[] = []

  addLogger(logger: Logger) {
    this.loggers.push(logger)
  }

  captureEvent(event: LogEvent) {
    this.loggers.forEach((logger) => {
      try {
        logger.captureEvent(event)
      } catch (error) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.error('Logger error:', error)
        }
      }
    })
  }

  captureException(error: Error, context?: Record<string, unknown>) {
    this.loggers.forEach((logger) => {
      try {
        logger.captureException(error, context)
      } catch (loggerError) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.error('Logger error:', loggerError)
        }
      }
    })
  }
}

const compositeLogger = new CompositeLogger()

/**
 * Register a Logger implementation. Call this at host-app boot, e.g. to
 * plug in Sentry:
 *
 * ```ts
 * import * as Sentry from '@sentry/react'
 * import { registerLogger } from '@twistopayments/design-system/utils'
 *
 * registerLogger({
 *   captureEvent: (event) => Sentry.captureEvent({ ... }),
 *   captureException: (error, context) => Sentry.captureException(error, { extra: context }),
 * })
 * ```
 */
export const registerLogger = (logger: Logger) => {
  compositeLogger.addLogger(logger)
}

const logError = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'error',
    extra,
  })
}

const logWarning = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'warning',
    extra,
  })
}

const logInfo = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'info',
    extra,
  })
}

const logDebug = (message: string, extra?: Record<string, unknown>) => {
  compositeLogger.captureEvent({
    message,
    level: 'debug',
    extra,
  })
}

const logException = (error: Error, context?: Record<string, unknown>) => {
  compositeLogger.captureException(error, context)
}

export const logger = {
  logDebug,
  logError,
  logException,
  logInfo,
  logWarning,
}
